import '../css/app.css';
import { createApp, h } from 'vue';
import { createStore } from 'vuex';
import { createPinia } from 'pinia';
import { createI18n } from 'vue-i18n';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from 'ziggy-js';
import Store from './vue/store/Store';
import mitt from 'mitt';
import HighchartsVue from 'highcharts-vue';
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import { setupCsrfInterceptor } from './utils/axiosCsrfInterceptor';
import { setupPrecognition } from './utils/setupPrecognition';
import { registerInertiaFlashListener, setupResponseToastInterceptor } from './utils/flashToast';
import { getAssetAllowUrls, shouldIgnoreError } from './utils/sentryErrorFilter';
import { registerUserScopedStorageCleanup } from './utils/userScopedStorage';
import { setLocale } from '@/composables/useLocale.js';
import { registerInertiaNetworkErrorListener } from './utils/inertiaNetworkErrors';
import { configureBroadcasting } from '@/features/Broadcast/echo';

/*
|--------------------------------------------------------------------------
| DATALAYER
|--------------------------------------------------------------------------
*/
window.dataLayer = window.dataLayer || [];
window.dataLayer.push({
    originalLocation:
        document.location.protocol +
        '//' +
        document.location.hostname +
        document.location.pathname +
        document.location.search,
});

/*
|--------------------------------------------------------------------------
| SHARED CONFIGURATION
|--------------------------------------------------------------------------
*/

let appName = import.meta.env.VITE_APP_NAME || 'GRAND';

// Create Vuex store (legacy - to be migrated)
const store = createStore(Store);

// Create Pinia store
const pinia = createPinia();

// Setup emitter
const emitter = mitt();

/*
|--------------------------------------------------------------------------
| LANGUAGE
|--------------------------------------------------------------------------
*/

const languageFilePaths = {};

Object.entries(import.meta.glob('../../lang/*.json')).forEach(([filePath, moduleSpecifier]) => {
    const lang = filePath.match(/\/lang\/([^/]+)\.json$/)?.[1];
    if (lang && !lang.includes('deleted')) {
        languageFilePaths[lang] = moduleSpecifier();
    }
});

const loadLanguage = async (lang) => {
    try {
        const messages = await languageFilePaths[lang];
        return messages.default;
    } catch {
        const fallbackMessages = languageFilePaths['en'];
        return fallbackMessages.default;
    }
};

const getLanguage = () => {
    return document.documentElement.lang || 'en';
};

const i18n = createI18n({
    legacy: false, // use legacy API
    allowComposition: true, // to use with Composition API
    locale: getLanguage(),
    fallbackLocale: 'en',
    messages: {
        [getLanguage()]: await loadLanguage(getLanguage()),
    },
    globalInjection: true,
    silentTranslationWarn: true,
});

const vendor = () => {
    // Extract the vendor name from the URL
    const currentUrl = window.location.pathname;
    const urlParts = currentUrl.split('/');
    const vendorIndex = urlParts.indexOf('vendors');
    // Check if "vendors" is found in the URL and if there is a vendor name after it
    return vendorIndex !== -1 && urlParts.length > vendorIndex + 1 ? urlParts[vendorIndex + 1] : null;
};

/*
|--------------------------------------------------------------------------
| Global window properties
|--------------------------------------------------------------------------
*/

window.axios = axios;
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
window.axios.defaults.withCredentials = true;
window.axios.defaults.withXSRFToken = true;
setupCsrfInterceptor(axios);
setupResponseToastInterceptor(axios, emitter);
setupPrecognition();
configureBroadcasting();

/*
|--------------------------------------------------------------------------
| INITIALIZE APPLICATIONS
|--------------------------------------------------------------------------
*/
createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        setLocale(props.initialPage.props.locale?.bcp47);

        const app = createApp({ render: () => h(App, props) });

        if (import.meta.env.VITE_SENTRY_ENABLED) {
            Sentry.init({
                app,
                dsn: import.meta.env.VITE_SENTRY_DSN,
                environment: import.meta.env.MODE,
                tunnel: '/sentry/tunnel',
                tracesSampleRate: 1.0,
                debug: import.meta.env.VITE_SENTRY_DEBUG,
                // This sets the sample rate to be 10%. You may want this to be 100% while
                // in development and sample at a lower rate in production
                replaysSessionSampleRate: 0.1,
                // If the entire session is not sampled, use the below sample rate to sample
                // sessions when an error occurs.
                replaysOnErrorSampleRate: 1.0,
                // Drops browser-injected scripts, which `window.onerror` reports against the
                // document URL. Built assets only: the dev server serves chunks from paths
                // the entry URL doesn't cover.
                allowUrls: import.meta.env.PROD ? getAssetAllowUrls(import.meta.url) : [],
                beforeSend(event, hint) {
                    // Drop aborted/cancelled/unauthenticated request errors that call sites
                    // don't consistently catch (Inertia navigation aborts, expired sessions)
                    if (shouldIgnoreError(hint?.originalException)) {
                        return null;
                    }
                    return event;
                },
                integrations: [
                    Sentry.replayIntegration({
                        // Additional SDK configuration goes in here, for example:
                        maskAllText: true,
                        blockAllMedia: true,
                        mutationBreadcrumbLimit: 1000,
                        mutationLimit: 1500,
                    }),
                ],
            });

            // Set global flag for components to check
            window.__sentry_enabled = true;
        }

        if (props.initialPage.props?.tenant?.isTenant) {
            appName = props.initialPage.props?.vendor?.name;
        }

        app.use(plugin);
        app.use(i18n);
        app.use(store); // Vuex (legacy)
        app.use(pinia); // Pinia
        app.use(ZiggyVue);
        app.use(HighchartsVue);

        app.config.errorHandler = (error, instance, info) => {
            // Send to Sentry if enabled
            if (import.meta.env.VITE_SENTRY_ENABLED) {
                Sentry.withScope((scope) => {
                    scope.setTag('errorBoundary', 'global');
                    scope.setContext('vue', {
                        componentName: instance?.$options.name || instance?.$options.__name || 'Unknown',
                        errorInfo: info,
                        propsData: instance?.$props,
                    });
                    scope.setLevel('error');
                    Sentry.captureException(error);
                });
            } else {
                console.error('Vue error:', error, 'Component:', instance, 'Info:', info);
            }
        };

        // Global get vendor function used in mixind and components in old way. To be removed in the future and use inertia props instead.
        app.config.globalProperties.getCurrentVendor = function () {
            return vendor();
        };
        app.provide('emitter', emitter);
        //Uncomment this to see better Vue warnings in the console
        // app.config.warnHandler= (msg, vm, trace) => {
        //     console.warn(`[Vue warn]: ${msg}${trace}`);
        // },
        app.mount(el);
    },
    progress: {
        color: '#4B5563',
    },
});

async function updateLanguage(language) {
    if (language && document.documentElement.lang !== language) {
        // Update HTML lang
        document.documentElement.lang = language;

        try {
            // Load the language if not already available
            if (!i18n.global.availableLocales.includes(language)) {
                const messages = await loadLanguage(language);
                i18n.global.setLocaleMessage(language, messages);
            }

            // vue-i18n calls its key a locale; ours is a bare language code.
            i18n.global.locale.value = language;
        } catch (error) {
            console.error(`Failed to update i18n language to ${language}:`, error);
        }
    }
}

registerInertiaFlashListener(router, emitter);
registerInertiaNetworkErrorListener(router, emitter);
registerUserScopedStorageCleanup();

router.on('navigate', async (event) => {
    const { locale } = event.detail.page.props;

    // The region the reader formats with, which the language alone cannot carry.
    setLocale(locale?.bcp47);

    if (locale?.language) {
        await updateLanguage(locale.language);
    }
});
